import { fetchConToken } from "../utils/fetch.ts";

// ========================================
// TIPOS Y INTERFACES
// ========================================

export interface GastoFormData {
  anio_presupuesto: number;
  tipo_id: number;
  categoria_id: number;
  subcategoria_id: number;
  descripcion: string;
  monto: number;
  fecha_gasto: string;
  responsable: string;
  fondo_id?: number;
  documento_adjunto?: string;
  observaciones?: string;
}

export interface Categoria {
  cat_id: number;
  cat_codigo: string;
  cat_nombre: string;
  cat_descripcion?: string;
  cat_color: string;
  cat_activo: boolean;
}

export interface Subcategoria {
  sub_id: number;
  sub_codigo: string;
  sub_nombre: string;
  sub_descripcion?: string;
  sub_activo: boolean;
  cat_id?: number;
  categoria_codigo?: string;
  categoria_nombre?: string;
  cat_color?: string;
}

export interface TipoGasto {
  tipo_id: number;
  tipo_codigo: string;
  tipo_nombre: string;
  tipo_descripcion?: string;
  tipo_activo: boolean;
}

export interface Fondo {
  fon_id: number;
  fon_codigo: string;
  fon_nombre: string;
  fon_descripcion?: string;
  fon_presupuesto_anual: number;
  fon_anio: number;
  fon_activo: boolean;
}

export interface Gasto {
  gas_id: number;
  gas_codigo: string;
  gas_anio_presupuesto: number;
  gas_tipo: string;
  gas_descripcion: string;
  gas_monto: number;
  gas_fecha_gasto: string;
  gas_fecha_ingreso: string;
  gas_responsable: string;
  gas_documento_adjunto?: string;
  gas_observaciones?: string;
  gas_estado: 'Pendiente' | 'Aprobado' | 'Rechazado';
  categoria_nombre: string;
  categoria_codigo: string;
  categoria_color: string;
  subcategoria_nombre: string;
  subcategoria_codigo: string;
  fondo_nombre?: string;
  fondo_codigo?: string;
}

export interface GastoFijo {
  gf_id: number;
  gf_nombre: string;
  gf_monto: number;
  gf_pais: string;
  gf_empresa?: string;
  gf_metodo_pago?: string;
  gf_frecuencia: string;
  gf_dia_vencimiento?: number;
  gf_grupo: 'Personal' | 'Familiar' | 'Negocio';
  gf_observaciones?: string;
  gf_activo: boolean;
  gf_fecha_creacion: string;
  tipo_id: number;
  tipo_codigo: string;
  tipo_nombre: string;
  categoria_id: number;
  categoria_codigo: string;
  categoria_nombre: string;
  categoria_color: string;
  subcategoria_id: number;
  subcategoria_codigo: string;
  subcategoria_nombre: string;
}

export interface GastoFijoFormData {
  gf_nombre: string;
  gf_monto: number;
  gf_tipo_id: number;
  gf_categoria_id: number;
  gf_subcategoria_id: number;
  gf_pais?: string;
  gf_empresa?: string;
  gf_metodo_pago?: string;
  gf_frecuencia?: string;
  gf_dia_vencimiento?: number;
  gf_grupo?: string;
  gf_observaciones?: string;
}

// ========================================
// FUNCIONES DE CATEGORÍAS
// ========================================

export const getCategorias = async (): Promise<{ success: boolean; data: Categoria[] }> => {
  try {
    const resp = await fetchConToken("gastos/categorias", {}, "GET");
    const body = await resp.json();
    return body;
  } catch (error) {
    console.error('Error al obtener categorías:', error);
    return { success: false, data: [] };
  }
};

export const getSubcategorias = async (categoriaId?: number): Promise<{ success: boolean; data: Subcategoria[] }> => {
  try {
    const endpoint = categoriaId 
      ? `gastos/subcategorias/${categoriaId}` 
      : "gastos/subcategorias";
    const resp = await fetchConToken(endpoint, {}, "GET");
    const body = await resp.json();
    return body;
  } catch (error) {
    console.error('Error al obtener subcategorías:', error);
    return { success: false, data: [] };
  }
};

export const getTiposGastos = async (): Promise<{ success: boolean; data: TipoGasto[] }> => {
  try {
    const resp = await fetchConToken("gastos/tipos-gastos", {}, "GET");
    const body = await resp.json();
    return body;
  } catch (error) {
    console.error('Error al obtener tipos de gastos:', error);
    return { success: false, data: [] };
  }
};

export const crearCategoria = async (categoriaData: {
  cat_codigo: string;
  cat_nombre: string;
  cat_descripcion?: string;
  cat_color?: string;
  cat_activo: boolean;
}): Promise<{ success: boolean; message: string; data?: Categoria }> => {
  try {
    const resp = await fetchConToken("gastos/categorias", categoriaData, "POST");
    const body = await resp.json();
    return body;
  } catch (error) {
    console.error('Error al crear categoría:', error);
    return { 
      success: false, 
      message: 'Error al conectar con el servidor' 
    };
  }
};

export const actualizarCategoria = async (
  id: number, 
  categoriaData: Partial<{
    cat_codigo: string;
    cat_nombre: string;
    cat_descripcion?: string;
    cat_color?: string;
    cat_activo: boolean;
  }>
): Promise<{ success: boolean; message: string }> => {
  try {
    const resp = await fetchConToken(`gastos/categorias/${id}`, categoriaData, "PUT");
    const body = await resp.json();
    return body;
  } catch (error) {
    console.error('Error al actualizar categoría:', error);
    return { 
      success: false, 
      message: 'Error al conectar con el servidor' 
    };
  }
};

export const eliminarCategoria = async (id: number): Promise<{ 
  success: boolean; 
  message: string 
}> => {
  try {
    const resp = await fetchConToken(`gastos/categorias/${id}`, {}, "DELETE");
    const body = await resp.json();
    return body;
  } catch (error) {
    console.error('Error al eliminar categoría:', error);
    return { 
      success: false, 
      message: 'Error al conectar con el servidor' 
    };
  }
};

export const toggleEstadoCategoria = async (id: number): Promise<{ 
  success: boolean; 
  message: string 
}> => {
  try {
    const resp = await fetchConToken(`gastos/categorias/${id}/toggle`, {}, "PATCH");
    const body = await resp.json();
    return body;
  } catch (error) {
    console.error('Error al cambiar estado de la categoría:', error);
    return { 
      success: false, 
      message: 'Error al conectar con el servidor' 
    };
  }
};

export const crearSubcategoria = async (subcategoriaData: {
  sub_categoria_id: number;
  sub_codigo: string;
  sub_nombre: string;
  sub_descripcion?: string;
  sub_activo: boolean;
}): Promise<{ success: boolean; message: string; data?: Subcategoria }> => {
  try {
    const resp = await fetchConToken("gastos/subcategorias", subcategoriaData, "POST");
    const body = await resp.json();
    return body;
  } catch (error) {
    console.error('Error al crear subcategoría:', error);
    return { 
      success: false, 
      message: 'Error al conectar con el servidor' 
    };
  }
};

export const actualizarSubcategoria = async (
  id: number, 
  subcategoriaData: Partial<{
    sub_codigo: string;
    sub_nombre: string;
    sub_descripcion?: string;
    sub_activo: boolean;
  }>
): Promise<{ success: boolean; message: string }> => {
  try {
    const resp = await fetchConToken(`gastos/subcategorias/${id}`, subcategoriaData, "PUT");
    const body = await resp.json();
    return body;
  } catch (error) {
    console.error('Error al actualizar subcategoría:', error);
    return { 
      success: false, 
      message: 'Error al conectar con el servidor' 
    };
  }
};

export const eliminarSubcategoria = async (id: number): Promise<{ 
  success: boolean; 
  message: string 
}> => {
  try {
    const resp = await fetchConToken(`gastos/subcategorias/${id}`, {}, "DELETE");
    const body = await resp.json();
    return body;
  } catch (error) {
    console.error('Error al eliminar subcategoría:', error);
    return { 
      success: false, 
      message: 'Error al conectar con el servidor' 
    };
  }
};

export const toggleEstadoSubcategoria = async (id: number): Promise<{ 
  success: boolean; 
  message: string 
}> => {
  try {
    const resp = await fetchConToken(`gastos/subcategorias/${id}/toggle`, {}, "PATCH");
    const body = await resp.json();
    return body;
  } catch (error) {
    console.error('Error al cambiar estado de la subcategoría:', error);
    return { 
      success: false, 
      message: 'Error al conectar con el servidor' 
    };
  }
};

// ========================================
// FUNCIONES DE FONDOS
// ========================================

export const getFondos = async (anio: number): Promise<{ success: boolean; data: Fondo[] }> => {
  try {
    const resp = await fetchConToken(`gastos/fondos/${anio}`, {}, "GET");
    const body = await resp.json();
    return body;
  } catch (error) {
    console.error('Error al obtener fondos:', error);
    return { success: false, data: [] };
  }
};

export const crearFondo = async (fondoData: {
  fon_codigo: string;
  fon_nombre: string;
  fon_descripcion?: string;
  fon_presupuesto_anual: number;
  fon_anio: number;
  fon_activo: boolean;
}): Promise<{ success: boolean; message: string; data?: Fondo }> => {
  try {
    const resp = await fetchConToken("gastos/fondos", fondoData, "POST");
    const body = await resp.json();
    return body;
  } catch (error) {
    console.error('Error al crear fondo:', error);
    return { 
      success: false, 
      message: 'Error al conectar con el servidor' 
    };
  }
};

export const actualizarFondo = async (
  id: number, 
  fondoData: Partial<{
    fon_codigo: string;
    fon_nombre: string;
    fon_descripcion?: string;
    fon_presupuesto_anual: number;
    fon_anio: number;
    fon_activo: boolean;
  }>
): Promise<{ success: boolean; message: string }> => {
  try {
    const resp = await fetchConToken(`gastos/fondos/${id}`, fondoData, "PUT");
    const body = await resp.json();
    return body;
  } catch (error) {
    console.error('Error al actualizar fondo:', error);
    return { 
      success: false, 
      message: 'Error al conectar con el servidor' 
    };
  }
};

export const eliminarFondo = async (id: number): Promise<{ 
  success: boolean; 
  message: string 
}> => {
  try {
    const resp = await fetchConToken(`gastos/fondos/${id}`, {}, "DELETE");
    const body = await resp.json();
    return body;
  } catch (error) {
    console.error('Error al eliminar fondo:', error);
    return { 
      success: false, 
      message: 'Error al conectar con el servidor' 
    };
  }
};

export const toggleEstadoFondo = async (id: number): Promise<{ 
  success: boolean; 
  message: string 
}> => {
  try {
    const resp = await fetchConToken(`gastos/fondos/${id}/toggle`, {}, "PATCH");
    const body = await resp.json();
    return body;
  } catch (error) {
    console.error('Error al cambiar estado del fondo:', error);
    return { 
      success: false, 
      message: 'Error al conectar con el servidor' 
    };
  }
};

// ========================================
// FUNCIONES DE GASTOS
// ========================================

export const crearGasto = async (gastoData: GastoFormData): Promise<{ 
  success: boolean; 
  message: string; 
  data?: any 
}> => {
  try {
    const resp = await fetchConToken("gastos", gastoData, "POST");
    const body = await resp.json();
    return body;
  } catch (error) {
    console.error('Error al crear gasto:', error);
    return { 
      success: false, 
      message: 'Error al conectar con el servidor' 
    };
  }
};

export const getGastos = async (filtros?: {
  anio?: number;
  categoria?: string;
  estado?: string;
  limite?: number;
  offset?: number;
}): Promise<{ success: boolean; data: Gasto[]; total?: number }> => {
  try {
    let endpoint = "gastos";
    const params = new URLSearchParams();
    
    if (filtros) {
      Object.entries(filtros).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, String(value));
        }
      });
    }
    
    if (params.toString()) {
      endpoint += `?${params.toString()}`;
    }
    
    const resp = await fetchConToken(endpoint, {}, "GET");
    const body = await resp.json();
    return body;
  } catch (error) {
    console.error('Error al obtener gastos:', error);
    return { success: false, data: [] };
  }
};

export const getGastoPorCodigo = async (codigo: string): Promise<{ 
  success: boolean; 
  data?: Gasto 
}> => {
  try {
    const resp = await fetchConToken(`gastos/codigo/${codigo}`, {}, "GET");
    const body = await resp.json();
    return body;
  } catch (error) {
    console.error('Error al obtener gasto por código:', error);
    return { success: false };
  }
};

export const getGasto = async (id: number): Promise<{ 
  success: boolean; 
  data?: Gasto 
}> => {
  try {
    const resp = await fetchConToken(`gastos/${id}`, {}, "GET");
    const body = await resp.json();
    return body;
  } catch (error) {
    console.error('Error al obtener gasto:', error);
    return { success: false };
  }
};

export const actualizarGasto = async (
  id: number, 
  gastoData: Partial<GastoFormData>
): Promise<{ success: boolean; message: string }> => {
  try {
    const resp = await fetchConToken(`gastos/${id}`, gastoData, "PUT");
    const body = await resp.json();
    return body;
  } catch (error) {
    console.error('Error al actualizar gasto:', error);
    return { 
      success: false, 
      message: 'Error al conectar con el servidor' 
    };
  }
};

export const eliminarGasto = async (id: number): Promise<{ 
  success: boolean; 
  message: string 
}> => {
  try {
    const resp = await fetchConToken(`gastos/${id}`, {}, "DELETE");
    const body = await resp.json();
    return body;
  } catch (error) {
    console.error('Error al eliminar gasto:', error);
    return { 
      success: false, 
      message: 'Error al conectar con el servidor' 
    };
  }
};

// ========================================
// FUNCIONES DE CÓDIGOS
// ========================================

export const previewCodigoGasto = async (anio: number): Promise<{ 
  success: boolean; 
  data?: { codigo_preview: string; anio: number; formato: string } 
}> => {
  try {
    const resp = await fetchConToken(`gastos/codigo/preview/${anio}`, {}, "GET");
    const body = await resp.json();
    return body;
  } catch (error) {
    console.error('Error al obtener preview de código:', error);
    return { success: false };
  }
};

export const verificarCodigoGasto = async (codigo: string): Promise<{ 
  success: boolean; 
  data?: { codigo: string; existe: boolean; mensaje: string } 
}> => {
  try {
    const resp = await fetchConToken(`gastos/codigo/verificar/${codigo}`, {}, "GET");
    const body = await resp.json();
    return body;
  } catch (error) {
    console.error('Error al verificar código:', error);
    return { success: false };
  }
};

export const getEstadisticasCodigos = async (anio?: number): Promise<{ 
  success: boolean; 
  data: Array<{
    anio: number;
    total_gastos: number;
    primer_codigo: string;
    ultimo_codigo: string;
    ultimo_numero: number;
  }> 
}> => {
  try {
    let endpoint = "gastos/estadisticas/codigos";
    if (anio) {
      endpoint += `?anio=${anio}`;
    }
    
    const resp = await fetchConToken(endpoint, {}, "GET");
    const body = await resp.json();
    return body;
  } catch (error) {
    console.error('Error al obtener estadísticas de códigos:', error);
    return { success: false, data: [] };
  }
};

// ========================================
// FUNCIONES DE REPORTES Y DASHBOARD
// ========================================

export const getReportesGastos = async (anio: number): Promise<{ 
  success: boolean; 
  data?: any 
}> => {
  try {
    const resp = await fetchConToken(`gastos/reportes/${anio}`, {}, "GET");
    const body = await resp.json();
    return body;
  } catch (error) {
    console.error('Error al obtener reportes:', error);
    return { success: false };
  }
};

export const getDashboardGastos = async (anio: number): Promise<{ 
  success: boolean; 
  data?: any 
}> => {
  try {
    const resp = await fetchConToken(`gastos/dashboard/${anio}`, {}, "GET");
    const body = await resp.json();
    return body;
  } catch (error) {
    console.error('Error al obtener dashboard:', error);
    return { success: false };
  }
};

// ========================================
// FUNCIONES DE GASTOS FIJOS/RECURRENTES
// ========================================

export const getGastosFijos = async (filtros?: {
  grupo?: string;
  activo?: boolean;
}): Promise<{ success: boolean; data: GastoFijo[] }> => {
  try {
    let endpoint = "gastos/gastos-fijos";
    const params = new URLSearchParams();
    
    if (filtros?.grupo) params.append('grupo', filtros.grupo);
    if (filtros?.activo !== undefined) params.append('activo', String(filtros.activo));
    
    if (params.toString()) {
      endpoint += `?${params.toString()}`;
    }
    
    const resp = await fetchConToken(endpoint, {}, "GET");
    const body = await resp.json();
    return body;
  } catch (error) {
    console.error('Error al obtener gastos fijos:', error);
    return { success: false, data: [] };
  }
};

export const getGastoFijo = async (id: number): Promise<{ 
  success: boolean; 
  data?: GastoFijo;
  message?: string;
}> => {
  try {
    const resp = await fetchConToken(`gastos/gastos-fijos/${id}`, {}, "GET");
    const body = await resp.json();
    return body;
  } catch (error) {
    console.error('Error al obtener gasto fijo:', error);
    return { 
      success: false, 
      message: 'Error al conectar con el servidor' 
    };
  }
};

export const crearGastoFijo = async (gastoFijoData: GastoFijoFormData): Promise<{ 
  success: boolean; 
  message: string;
  data?: GastoFijo;
}> => {
  try {
    const resp = await fetchConToken("gastos/gastos-fijos", gastoFijoData, "POST");
    const body = await resp.json();
    return body;
  } catch (error) {
    console.error('Error al crear gasto fijo:', error);
    return { 
      success: false, 
      message: 'Error al conectar con el servidor' 
    };
  }
};

export const actualizarGastoFijo = async (
  id: number, 
  gastoFijoData: Partial<GastoFijoFormData>
): Promise<{ success: boolean; message: string; data?: GastoFijo }> => {
  try {
    const resp = await fetchConToken(`gastos/gastos-fijos/${id}`, gastoFijoData, "PUT");
    const body = await resp.json();
    return body;
  } catch (error) {
    console.error('Error al actualizar gasto fijo:', error);
    return { 
      success: false, 
      message: 'Error al conectar con el servidor' 
    };
  }
};

export const eliminarGastoFijo = async (id: number): Promise<{ 
  success: boolean; 
  message: string 
}> => {
  try {
    const resp = await fetchConToken(`gastos/gastos-fijos/${id}`, {}, "DELETE");
    const body = await resp.json();
    return body;
  } catch (error) {
    console.error('Error al eliminar gasto fijo:', error);
    return { 
      success: false, 
      message: 'Error al conectar con el servidor' 
    };
  }
};

export const toggleEstadoGastoFijo = async (id: number): Promise<{ 
  success: boolean; 
  message: string;
  data?: GastoFijo;
}> => {
  try {
    const resp = await fetchConToken(`gastos/gastos-fijos/${id}/toggle`, {}, "PATCH");
    const body = await resp.json();
    return body;
  } catch (error) {
    console.error('Error al cambiar estado del gasto fijo:', error);
    return { 
      success: false, 
      message: 'Error al conectar con el servidor' 
    };
  }
};

export const crearGastoDesdeGastoFijo = async (
  id: number,
  datosAdicionales: {
    anio_presupuesto?: number;
    fecha_gasto?: string;
    monto?: number;
    descripcion?: string;
    responsable: string;
    fondo_id?: number;
    observaciones?: string;
  }
): Promise<{ success: boolean; message: string; data?: Gasto }> => {
  try {
    const resp = await fetchConToken(
      `gastos/gastos-fijos/${id}/crear-gasto`, 
      datosAdicionales, 
      "POST"
    );
    const body = await resp.json();
    return body;
  } catch (error) {
    console.error('Error al crear gasto desde plantilla:', error);
    return { 
      success: false, 
      message: 'Error al conectar con el servidor' 
    };
  }
};